/**
 * 반응형 calc() 유틸리티
 * 
 * 기준 해상도:
 * - 데스크톱: 1920px
 * - 태블릿: 768px
 * - 모바일: 375px (iPhone SE 기준, 평균적인 모바일 가로 너비)
 */

export const breakpoints = {
  mobile: 375,
  tablet: 768,
  desktop: 1920,
} as const;

export const mediaQueries = {
  mobile: `@media (max-width: ${breakpoints.tablet - 1}px)`,
  tablet: `@media (min-width: ${breakpoints.tablet}px) and (max-width: 1199px)`,
  desktop: `@media (min-width: 1200px)`,
  tabletUp: `@media (min-width: ${breakpoints.tablet}px)`,
  desktopUp: `@media (min-width: 1200px)`,
} as const;

/**
 * 반응형 크기값 생성 함수
 * 각 브레이크포인트별 값을 받아서 미디어쿼리 없이도 적절히 스케일되는 clamp 생성
 * @param desktopVal - 데스크톱 1920px 기준 값
 * @param mobileVal - 모바일 375px 기준 값 (기본값으로도 사용)
 * @param tabletVal - 태블릿 768px 기준 값 (선택, 없으면 중간값)
 */
export const rv = (desktopVal: number, mobileVal?: number, _tabletVal?: number): string => {
  const mobile = mobileVal ?? Math.round(desktopVal * 0.6);
  
  // 1920px에서 desktopVal, 375px에서 mobileVal이 되도록 선형 보간
  // y = mx + b 형태로: vwCoeff * vw + baseVal
  const vwCoeff = ((desktopVal - mobile) / (breakpoints.desktop - breakpoints.mobile)) * 100;
  const baseVal = mobile - (vwCoeff * breakpoints.mobile / 100);
  
  return `clamp(${mobile}px, calc(${vwCoeff.toFixed(4)}vw + ${baseVal.toFixed(4)}px), ${desktopVal}px)`;
};

/**
 * 폰트 전용 반응형 함수 (rv의 alias)
 */
export const fontSize = rv;

/**
 * 간격 전용 반응형 함수 (rv의 alias)
 */
export const spacing = rv;

/**
 * 단순 px 값 (반응형 아님, 고정값)
 */
export const px = (value: number): string => `${value}px`;

/**
 * rem 변환 (16px 기준)
 */
export const rem = (value: number): string => `${value / 16}rem`;

/**
 * 컨테이너 너비 전용 반응형 함수
 * @param maxWidth - 최대 너비 (px)
 * @param padding - 좌우 패딩 (px)
 */
export const containerWidth = (maxWidth: number, padding: number = 24): string => {
  return `min(${maxWidth}px, calc(100vw - ${padding * 2}px))`;
};

/**
 * 반응형 스타일 생성 헬퍼 (미디어쿼리 사용)
 * 각 브레이크포인트별로 다른 스타일을 적용할 때 사용
 */
export const responsiveStyles = (
  mobileVal: string | number,
  tabletVal?: string | number,
  desktopVal?: string | number
): string => {
  const format = (val: string | number) => typeof val === 'number' ? `${val}px` : val;
  const tablet = tabletVal ?? mobileVal;
  const desktop = desktopVal ?? tablet;

  return `
    ${format(mobileVal)};
    
    ${mediaQueries.tablet} {
      ${format(tablet)};
    }
    
    ${mediaQueries.desktop} {
      ${format(desktop)};
    }
  `;
};
